import React, { FC, useMemo, useRef } from 'react'
import { GoToTop, styled, Toolbar, useGoToTop } from '~/app/components'
import CancelCard from './CancelCard'
import { InformationFormat } from '~/app/components/order-list/details/InformationPanel'
import CancellationCategoryOrderList from '~/app/components/order-list/details/CancelInformation/CancellationCategoryOrderList'
import { OrderDetailsCancellationCategoryOrders } from '~/app/components/order-list/hooks/useCancelInfo'
import { adminCancelReason, cancelReason } from '~/app/utils'
import useToolbarScroll from '~/app/components/common/Toolbar/useToolbarScroll'
import { CancelCause } from '~/__generated__/__types__'
import SubHeader from '~/app/components/common/SubHeader/SubHeader'
import { Typography } from '@yanolja-rnd/ya-com-core'
import { Layout, ContentContainer, Content } from '~/app/components/layout-style'
import { Detail } from '~/app/components/order-list/common/OrderDetailsCommonStyled'

/**
 * LeisureInformation component
 * author: soojin.oh
 */

// type definitions
type Props = {
  date: string
  cancelInformation: InformationFormat[]
  onClickBack: () => void
  categoryOrders?: OrderDetailsCancellationCategoryOrders
  cancelReasonCode: string
  cancelReasonDetail: string
  cancelCause?: CancelCause
  cancelReasonCodeName: string
}

// styled components
const ReasonContainer = styled.div`
  background: ${({ theme }) => theme.color.white};
  flex: 0 0 auto;
  padding-bottom: 2.4rem;
  border-bottom: 0.1rem solid ${({ theme: { color } }) => color.gray200};
  margin-bottom: 1.2rem;
`
const CardsContainer = styled.div`
  padding: 1rem;
`

const Date = styled.div`
  ${Typography('body1').regular};

  padding: 1.2rem 0 0 2rem;
`

// CancelRefundInformation component
const CancelInformation: FC<Props> = function ({
  date,
  cancelInformation,
  onClickBack,
  categoryOrders,
  cancelCause,
  cancelReasonCode,
  cancelReasonDetail,
  // cancelReasonCodeName,
}) {
  const scrollTarget = useRef<HTMLDivElement>(null)
  const { isFabOpen, onClick } = useGoToTop(scrollTarget)
  const { hasShadow } = useToolbarScroll(scrollTarget)

  /**
   * 유저가 취소한 경우에만 사유를 노출하고 그외에는 운영자 취소로 노출
   * 단, 운영자 취소이지만 일부 코드에 대해 유저에게 노출할 필요가 있음.
   * adminCancelReason 에 정의된 코드들은 사유를 노출함.
   */
  const cancelReasonText = useMemo(() => {
    if (!cancelCause) return ''
    return cancelCause === CancelCause.ByUser
      ? cancelReason[cancelReasonCode]
      : (adminCancelReason[cancelCause] ?? '운영자 취소')
  }, [cancelCause, cancelReasonCode])

  return (
    <Layout>
      <Toolbar
        type={'close'}
        hasShadow={hasShadow}
        title={'취소 및 환불 상세 정보'}
        onClickBackButton={onClickBack}
      />
      <ContentContainer>
        <Content ref={scrollTarget} style={{ justifyContent: 'flex-start' }}>
          <Date>{date}</Date>
          <CardsContainer>
            <CancelCard information={cancelInformation} />
          </CardsContainer>
          <ReasonContainer>
            <SubHeader title="취소사유" hasBottomContent />
            <Detail style={{ margin: '0 2rem' }}>
              <div className={'title'}>취소사유</div>
              <div className={'description'}>{cancelReasonText}</div>
            </Detail>
            {cancelReasonDetail && (
              <Detail style={{ margin: '1.1rem 2rem 0 2rem' }}>
                <div className={'title'}>상세사유</div>
                <div className={'description'}>{cancelReasonDetail}</div>
              </Detail>
            )}
          </ReasonContainer>
          <CancellationCategoryOrderList categoryOrders={categoryOrders} />
        </Content>
      </ContentContainer>
      <GoToTop isOpened={isFabOpen} onClick={onClick} />
    </Layout>
  )
}

export default CancelInformation
